import {
  PdfGen,
  PdfUnit,
  PdfSize,
  PdfPen,
  PdfColor,
  PdfPoint,
  PdfRect,
  PdfFont,
  PdfBrush,
  ETextAlign,
  PageGen,
} from '../../pdf/pdf';
import { ILayoutBaseGen } from '../layoutBase';
import { OptsBase } from '../optsBase';
import { getHolidays } from '../../core/calendar';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/cut';
import { GUtil } from '../../pdf/gutil';

export abstract class KalendarRok__Base implements ILayoutBaseGen {
  protected abstract get yearOffset(): number;

  public async generate(pdf: PdfGen, opts: OptsBase): Promise<void> {
    const page = pdf.addPage();

    const year = new Date().getFullYear() + this.yearOffset;
    const holidays = getHolidays(year, opts.data.langCode);

    const font = new PdfFont(PdfFont.FONT_NAME, 12);
    const headerFont = new PdfFont(PdfFont.FONT_NAME, 64);

    const yearHeight = PdfUnit.FromMillimeters(50);

    const labelRect = new PdfRect(0, 0, PdfSize.A4.width, yearHeight);
    const calendarRect = new PdfRect(
      0,
      yearHeight,
      PdfSize.A4.width,
      PdfSize.A4.height - yearHeight
    );

    const {lang} = opts.data;
    const monthNames = [
      lang.Kalendar_Month_leden ?? 'January',
      lang.Kalendar_Month_unor ?? 'February',
      lang.Kalendar_Month_brezen ?? 'March',
      lang.Kalendar_Month_duben ?? 'April',
      lang.Kalendar_Month_kveten ?? 'May',
      lang.Kalendar_Month_cerven ?? 'June',
      lang.Kalendar_Month_cervenec ?? 'July',
      lang.Kalendar_Month_srpen ?? 'August',
      lang.Kalendar_Month_zari ?? 'September',
      lang.Kalendar_Month_rijen ?? 'October',
      lang.Kalendar_Month_listopad ?? 'November',
      lang.Kalendar_Month_prosinec ?? 'December',
    ];

    page.drawString(`${year}`, headerFont, PdfBrush.Black, null, labelRect, ETextAlign.Center);

    this.drawYearCalendar(
      page,
      calendarRect,
      year,
      (dayData) => {
        const { day, rect } = dayData;
        const isHoliday = holidays.some(
          (h) => h.getDate() === day.getDate() && h.getMonth() === day.getMonth()
        );
        const isSunday = day.getDay() === 0;

        const brush =
          isHoliday || isSunday ? new PdfBrush(PdfColor.fromRgb(255, 0, 0)) : PdfBrush.Black;

        const centerRect = this.centerRect(rect);

        page.drawString(`${day.getDate()}`, font, brush, null, rect, ETextAlign.Center);
        page.drawEllipse(GUtil.basePen, null, centerRect);
      },
      monthNames
    );
  }

  protected drawYearCalendar(
    g: PageGen,
    rect: PdfRect,
    year: number,
    drawDay: (data: { g: PageGen; day: Date; rect: PdfRect }) => void,
    monthNames: string[]
  ): void {
    const margin = PdfUnit.FromMillimeters(10);
    const monthSize = new PdfSize(rect.width / 3, rect.height / 4);
    const monthLabelHeight = PdfUnit.FromMillimeters(10);
    const daySize = new PdfSize(
      (monthSize.width - 2 * margin) / 7,
      (monthSize.height - 2 * margin - monthLabelHeight) / 5
    );

    for (let x = 0; x < 3; x++) {
      for (let y = 0; y < 4; y++) {
        const month = y * 3 + x;

        const days = new Date(year, month + 1, 0).getDate();
        let week = 0;

        const monthRectX = rect.x + x * monthSize.width;
        const monthRectY = rect.y + y * monthSize.height;

        const labelRect = new PdfRect(
          monthRectX + margin,
          monthRectY + margin + week * daySize.height,
          monthSize.width - 2 * margin,
          monthLabelHeight
        );

        g.drawString(
          monthNames[month],
          new PdfFont(PdfFont.FONT_NAME, 12),
          PdfBrush.Black,
          null,
          labelRect,
          ETextAlign.CenterLeft
        );

        for (let d = 1; d <= days; d++) {
          const date = new Date(year, month, d);
          const dd = (date.getDay() + 6) % 7;

          const dayRect = new PdfRect(
            monthRectX + margin + dd * daySize.width,
            monthRectY + margin + monthLabelHeight + week * daySize.height,
            daySize.width,
            daySize.height
          );

          drawDay({
            g,
            day: date,
            rect: dayRect,
          });

          if (date.getDay() === 0) {
            week++;
          }
        }
      }
    }
  }

  protected centerRect(rect: PdfRect): PdfRect {
    const size = Math.min(rect.width, rect.height);
    return new PdfRect(
      rect.x + (rect.width - size) / 2,
      rect.y + (rect.height - size) / 2,
      size,
      size
    );
  }
}

export class KalendarRok__TentoRok extends KalendarRok__Base {
  protected get yearOffset(): number {
    return 0;
  }
}

export class KalendarRok__DalsiRok extends KalendarRok__Base {
  protected get yearOffset(): number {
    return 1;
  }
}
